import React, { useEffect, useRef } from "react";
import SectionHeader from "./SectionHeader";
import { AKR } from "../mock";

/**
 * AKR EMPIRE — premium full-bleed video-background section.
 * The holographic ship explosion video plays as the section's atmospheric backdrop;
 * content sits on top with a darkening + gold tint scrim so the type stays readable.
 */
export default function AKREmpire() {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    document.addEventListener("click", tryPlay, { once: true });
    return () => document.removeEventListener("click", tryPlay);
  }, []);

  return (
    <section className="relative section-pad overflow-hidden">
      {/* Video backdrop */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          style={{
            opacity: 0.92,
            filter:
              "saturate(0.85) contrast(1.05) brightness(1.05)",
            transform: "scale(1.06)",
            animation: "ship-pan 22s ease-in-out infinite alternate",
          }}
        >
          <source src="/videos/ship.webm" type="video/webm" />
          <source src="/videos/ship.mp4" type="video/mp4" />
        </video>
        {/* dark scrim — lighter in the middle band so the explosion stays visible */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(8,7,5,0.85) 0%, rgba(8,7,5,0.18) 40%, rgba(8,7,5,0.2) 60%, rgba(8,7,5,0.92) 100%)",
          }}
        />
        {/* warm gold overlay (subtle, doesn't kill the cyan) */}
        <div
          className="absolute inset-0 mix-blend-overlay pointer-events-none"
          style={{
            background:
              "linear-gradient(120deg, rgba(200,161,74,0.18) 0%, transparent 50%, rgba(106,53,34,0.18) 100%)",
          }}
        />
      </div>

      <div className="container-narrow relative">
        <SectionHeader chapter={AKR.chapter} label={AKR.label} heading={AKR.heading} />

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="font-serif text-[19px] leading-[1.75] text-[color:var(--bx-bone)]/95 [text-shadow:0_2px_18px_rgba(0,0,0,0.85)]">
              {AKR.body}
            </p>
            <div className="mt-8 inline-flex items-center gap-4 px-5 py-3 border border-[color:var(--bx-gold)]/40 bg-black/60 backdrop-blur-sm">
              <span className="bullet-mark" />
              <span className="font-sc text-[11px] gold-text tracking-[0.42em]">
                {"CONSIGNMENT \u00B7 ON THE WATER"}
              </span>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[color:var(--bx-gold)]/30">
              {AKR.meta.map((m, i) => (
                <div
                  key={i}
                  className="p-6 backdrop-blur-md"
                  style={{ background: "rgba(8,7,5,0.62)" }}
                >
                  <div className="font-sc text-[10px] gold-text tracking-[0.42em] mb-2">{m.k}</div>
                  <div className="font-serif text-[18px] text-[color:var(--bx-bone)]/95">{m.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
